/**
 * KundenPortal V3 — Modernes Kunden-Dashboard mit Lazy Registration
 *
 * DESIGN: Airbnb-inspiriert, Split-Screen, USPs, Trust-Building.
 * CI: Teal/Old-Turquoise (#3d7a8a), Plus Jakarta Sans, mikro-minimalistisch.
 *
 * LOGIN-OPTIONEN:
 *   Tab 1: Auftragsnummer + Nachname (bestehend)
 *   Tab 2: Kennzeichen + Nachname + Handynummer → OTP per SMS
 *   Gast-Zugang: "Portal entdecken" → Demo-Dashboard ohne Daten
 *
 * GUEST-MODUS: Nach Login sieht der Kunde sein Dashboard read-only.
 * Bei aktiver Handlung → Registration-Modal (Google / E-Mail).
 *
 * FOOTER: Unfallgiganten Branding als SaaS-Hinweis für andere Gutachter.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { STATUS_LABELS, type AktenStatus } from '@/lib/supabase';
import type { Akte, Dokument } from '@/lib/database.types';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Download, Clock, CheckCircle2,
  AlertCircle, ChevronRight, Car, Shield,
  Phone, Mail, MapPin, Loader2, ArrowLeft,
  Eye, LogOut, Search, User, Lock,
  Calendar, Building2, Scale, Wrench,
  FileCheck, ExternalLink, Home, Truck,
  MessageSquare, Send, RefreshCw, ChevronDown,
  Info, ArrowRight, Banknote, Camera, File,
  CircleDot, Smartphone, KeyRound, CheckCircle,
  Zap, Users, TrendingUp, X, FileUp, Star,
  Play, Award, Globe
} from 'lucide-react';

// ============ TYPES ============
interface KundenSession {
  akten_id: string;
  akten_nr: string;
  kunde_name: string;
  login_time: number;
  is_guest: boolean;
  is_visitor?: boolean; // Besucher ohne echten Fall
}

interface KundenNachricht {
  id: string;
  akte_id: string;
  absender: 'kunde' | 'buero' | 'system';
  nachricht: string;
  created_at: string;
  gelesen: boolean;
}

// ============ STATUS TIMELINE ============
const TIMELINE_STEPS = [
  { key: 'check_in', label: 'Check-in', desc: 'Ihr Fall wurde erfasst', icon: CircleDot },
  { key: 'begutachtung', label: 'Begutachtung', desc: 'Fahrzeug wird begutachtet', icon: Camera },
  { key: 'gutachten', label: 'Gutachten', desc: 'Gutachten wird erstellt', icon: FileText },
  { key: 'versendet', label: 'Versendet', desc: 'An Versicherung gesendet', icon: Send },
  { key: 'regulierung', label: 'Regulierung', desc: 'Auszahlung erfolgt', icon: Banknote },
];

function getTimelineProgress(status: AktenStatus, statusSv?: string | null): number {
  if (statusSv) {
    const sv = statusSv.toLowerCase();
    if (sv.includes('regulier') || sv.includes('abgeschlossen') || sv.includes('bezahlt')) return 5;
    if (sv.includes('versend') || sv.includes('warten')) return 4;
    if (sv.includes('gutachten') || sv.includes('erstell')) return 3;
    if (sv.includes('besichtig') || sv.includes('begutacht')) return 2;
    if (sv.includes('check') || sv.includes('erfass') || sv.includes('neu')) return 1;
  }
  switch (status) {
    case 'neu': return 1;
    case 'in_bearbeitung': return 2;
    case 'warten': return 4;
    case 'abgeschlossen': return 5;
    case 'storniert': return -1;
    default: return 0;
  }
}

function formatCurrency(value: number | null | undefined): string {
  if (!value && value !== 0) return '—';
  return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ============ USP DATA ============
const USP_ITEMS = [
  {
    icon: Shield,
    title: '100% kostenfrei',
    desc: 'Die gegnerische Versicherung trägt alle Kosten.',
  },
  {
    icon: Smartphone,
    title: 'Alles digital',
    desc: 'Kein Papierkram. Status jederzeit in Echtzeit.',
  },
  {
    icon: Scale,
    title: 'Top-Anwälte inklusive',
    desc: 'Unser Netzwerk kämpft für Ihr volles Recht.',
  },
  {
    icon: Zap,
    title: 'Schnelle Regulierung',
    desc: 'Durchschnittlich 40% schneller als der Branchendurchschnitt.',
  },
];

// ============ ECHTE GOOGLE BEWERTUNGEN ============
const REVIEWS = [
  {
    text: 'Perfekter Service! Sehr schnelle Reaktion. Stets sehr gut erreichbar. Professionelle und umfangreiche Gutachtenerstellung.',
    author: 'Elektro Wandelt',
    rating: 5,
  },
  {
    text: 'So eine Form der Kundenorientierung habe ich selten in diesem Bereich erlebt und habe es sehr positiv wahrgenommen.',
    author: 'Elisa Kramer',
    rating: 5,
  },
  {
    text: 'Das Gutachten ist der Hammer, nicht nur ein Brief mit zwei Seiten, sondern eine ganze Mappe wo alles erklärt und erläutert wird.',
    author: 'Slawa Seiz',
    rating: 5,
  },
  {
    text: 'Wir sind immer mehr als zufrieden gewesen. Top Service. Wir empfehlen das Büro gerne weiter!',
    author: 'Wowa ba',
    rating: 5,
  },
  {
    text: 'Auch der Anwalt erledigt seine Aufgaben auf dem höchsten Level. Kann ich nur weiterempfehlen!',
    author: 'Taras Bauer',
    rating: 5,
  },
  {
    text: 'Bin sehr zufrieden mit dem Ergebnis gewesen. Sehr gute Arbeit! Danke für das schnelle und detaillierte Gutachten.',
    author: 'Vladimir Pfafenrot',
    rating: 5,
  },
];

// ============ REGISTRATION MODAL ============
function RegistrationModal({ isOpen, onClose, onSuccess }: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'choose' | 'email'>('choose');

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin + '/kunde/dashboard' },
      });
      if (error) throw error;
    } catch (err: any) {
      toast.error(err.message || 'Fehler bei der Anmeldung');
      setLoading(false);
    }
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: { emailRedirectTo: window.location.origin + '/kunde/dashboard' },
      });
      if (error) throw error;
      toast.success('Bestätigungs-E-Mail gesendet! Bitte prüfen Sie Ihr Postfach.');
      onSuccess();
    } catch (err: any) {
      toast.error(err.message || 'Fehler bei der Registrierung');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 relative"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-[#3d7a8a]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-7 h-7 text-[#3d7a8a]" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Konto erstellen</h2>
            <p className="text-sm text-gray-500 mt-1">
              Um diese Aktion auszuführen, erstellen Sie bitte ein kostenloses Konto.
            </p>
          </div>

          {mode === 'choose' ? (
            <div className="space-y-3">
              <button
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 py-3.5 px-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium text-sm"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Mit Google fortfahren
              </button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-3 bg-white text-gray-400">oder</span>
                </div>
              </div>

              <button
                onClick={() => setMode('email')}
                className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-[#3d7a8a] text-white rounded-xl hover:bg-[#2d5a66] transition-colors font-medium text-sm"
              >
                <Mail className="w-4 h-4" />
                Mit E-Mail registrieren
              </button>
            </div>
          ) : (
            <form onSubmit={handleEmailSignUp} className="space-y-4">
              <button
                type="button"
                onClick={() => setMode('choose')}
                className="text-sm text-[#3d7a8a] hover:underline flex items-center gap-1 mb-2"
              >
                <ArrowLeft className="w-3 h-3" /> Zurück
              </button>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wider">E-Mail</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ihre@email.de"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3d7a8a]/20 focus:border-[#3d7a8a]"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wider">Passwort</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 Zeichen"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3d7a8a]/20 focus:border-[#3d7a8a]"
                  required
                  minLength={6}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 bg-[#3d7a8a] text-white rounded-xl hover:bg-[#2d5a66] disabled:opacity-50 transition-colors font-medium text-sm flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Konto erstellen'}
              </button>
            </form>
          )}

          <p className="text-center text-xs text-gray-400 mt-5">
            Mit der Registrierung akzeptieren Sie unsere{' '}
            <a href="https://gutachter-koop.de/datenschutz" className="underline">Datenschutzerklärung</a>.
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ============ STAR RATING COMPONENT ============
function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200'}`}
        />
      ))}
    </div>
  );
}

// ============ UNFALLGIGANTEN LOGO INLINE SVG ============
function UnfallgigantenLogo({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 161 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M63.3633 15.8378C63.3633 19.5569 60.8905 21.9092 56.9503 21.9092C53.0101 21.9092 50.457 19.5569 50.457 15.8378V7.67578H54.0353V15.8378C54.0353 17.7479 55.2613 18.914 56.9705 18.914C58.6798 18.914 59.8053 17.7479 59.8053 15.8378V7.67578H63.364V15.8378H63.3633Z" fill="#8F9192"/>
      <path d="M75.1649 21.7488L68.6911 13.0839V21.7488H65.334V7.67578H68.4903L74.9842 16.3609V7.67578H78.3219V21.7488H75.1655H75.1649Z" fill="#8F9192"/>
      <path d="M84.1102 10.4904V13.7471H90.4228V16.5617H84.1102V21.7488H80.5312V7.67578H90.9654V10.4904H84.1102Z" fill="#8F9192"/>
      <path d="M94.5847 19.1552L93.5595 21.7488H89.8809L95.9522 7.67578H99.6308L105.561 21.7488H101.742L100.737 19.1552H94.5847V19.1552ZM97.6803 11.2345L95.63 16.4411H99.6908L97.6803 11.2345Z" fill="#8F9192"/>
      <path d="M115.813 18.7529V21.7488H106.264V7.67578H109.843V18.7529H115.813V18.7529Z" fill="#8F9192"/>
      <path d="M126.467 18.7529V21.7488H116.918V7.67578H120.497V18.7529H126.468H126.467Z" fill="#8F9192"/>
      <path d="M63.1428 31.8536V37.3419C61.6351 38.4075 59.2626 39.1711 57.2925 39.1711C53.01 39.1711 49.7734 36.0755 49.7734 31.9735C49.7734 27.8716 53.0908 24.8164 57.5338 24.8164C59.6245 24.8164 61.8359 25.6205 63.2836 26.9071L61.2731 29.3396C60.248 28.3946 58.8205 27.8116 57.5136 27.8116C55.2018 27.8116 53.4326 29.6206 53.4326 31.9729C53.4326 34.3251 55.2213 36.1745 57.554 36.1745C58.2779 36.1745 59.2026 35.913 60.0471 35.4911V31.8522H63.1428V31.8536Z" fill="#464648"/>
      <path d="M65.0312 39.0137V24.9414H68.6102V39.0137H65.0312Z" fill="#464648"/>
      <path d="M83.2658 31.8536V37.3419C81.7581 38.4075 79.3856 39.1711 77.4156 39.1711C73.1337 39.1711 69.8965 36.0755 69.8965 31.9735C69.8965 27.8716 73.2132 24.8164 77.6562 24.8164C79.7469 24.8164 81.9583 25.6205 83.406 26.9071L81.3955 29.3396C80.3703 28.3946 78.9428 27.8116 77.636 27.8116C75.3242 27.8116 73.5549 29.6206 73.5549 31.9729C73.5549 34.3251 75.3444 36.1745 77.6764 36.1745C78.4003 36.1745 79.325 35.913 80.1695 35.4911V31.8522H83.2658V31.8536Z" fill="#464648"/>
      <path d="M88.351 36.4202L87.3258 39.0137H83.6465L89.7178 24.9414H93.3971L99.3276 39.0137H95.5081L94.5032 36.4202H88.351V36.4202ZM91.4466 28.4994L89.3963 33.706H93.4571L91.4466 28.4994Z" fill="#464648"/>
      <path d="M109.862 39.0137L103.388 30.3489V39.0137H100.031V24.9414H103.188L109.681 33.6265V24.9414H113.019V39.0137H109.863H109.862Z" fill="#464648"/>
      <path d="M121.743 27.8167V39.0144H118.165V27.8167H113.963V24.9219H125.985V27.8167H121.743V27.8167Z" fill="#464648"/>
      <path d="M130.487 27.7553V30.5497H137.201V33.3643H130.487V36.1991H138.146V39.0137H126.908V24.9414H137.925V27.756H130.487V27.7553Z" fill="#464648"/>
      <path d="M149.686 39.0137L143.213 30.3489V39.0137H139.855V24.9414H143.012L149.506 33.6265V24.9414H152.843V39.0137H149.686Z" fill="#464648"/>
      <path d="M0 13.6321V18.6263L3.79998 20.8451L11.2503 25.1951C11.3548 25.2564 11.433 25.3589 11.464 25.4775L12.7075 30.2083L18.4917 32.4318C19.0086 31.8494 20.3721 30.3235 21.8529 28.6972L14.1026 24.172C13.9395 24.0776 13.8533 23.8963 13.8782 23.711C13.9038 23.5276 14.0373 23.3746 14.2186 23.3275L16.2271 22.8011C16.2877 22.7842 16.3511 22.7808 16.4144 22.7916L23.3188 23.4232C20.5885 22.5052 15.6252 21.3331 13.3565 22.0206C16.3349 20.0781 19.564 21.813 25.2289 21.9478C23.4058 20.8815 15.0577 16.0018 8.77405 12.3292C7.19084 11.4059 5.47687 10.4063 3.76965 9.41016L1.77395 10.5627C0.793965 11.1261 0 12.5011 0 13.6321Z" fill="#B0172F"/>
      <path d="M40.4275 10.559L22.8733 0.424111C21.894 -0.14137 20.3061 -0.14137 19.3268 0.424111L15.7782 2.47305L24.8151 7.74975C25.0625 7.89466 25.3078 8.19796 25.607 10.3089C25.6967 10.9452 26.06 13.7557 26.3451 16.5872L32.0969 19.6323C32.1771 19.6767 32.2391 19.7455 32.2735 19.8311C32.3443 20.0009 32.9327 21.5039 32.2129 22.4233C31.489 23.3466 29.01 22.4873 28.2646 22.2009C28.2356 22.1907 28.2087 22.1759 28.181 22.1564C28.1123 22.1085 28.0577 22.0391 28.0287 21.9569L27.2597 19.7758L26.6025 19.5076C26.7043 20.9317 26.7427 22.1281 26.6598 22.732C27.0036 23.4713 27.1943 23.9896 27.2132 24.0402C27.2138 24.0429 27.2159 24.0462 27.2145 24.0489C27.2213 24.0644 27.2246 24.0813 27.228 24.0975C27.2314 24.1069 27.232 24.115 27.2354 24.1258C27.2381 24.1453 27.2368 24.1628 27.2395 24.1804C27.2395 24.1885 27.2401 24.1959 27.2415 24.204C27.2401 24.2275 27.2374 24.2525 27.232 24.2761C27.232 24.2781 27.2334 24.2808 27.232 24.2821C27.2267 24.3078 27.2206 24.3334 27.2112 24.3583L26.1988 27.0772C26.1914 27.1446 26.1658 27.2053 26.1321 27.2619L25.6333 28.6031C25.8611 28.7669 26.0708 28.9826 26.155 29.2737C26.2042 29.447 26.2244 29.7179 26.0438 30.0279L23.2407 34.8288L21.644 37.5625L19.8498 40.6353C19.7217 40.8557 19.4393 40.9305 19.2183 40.8004L13.6929 37.574C13.4718 37.4452 13.399 37.1635 13.5271 36.9438L14.4316 35.3943L0 26.9667V33.9C0 35.031 0.793965 36.4059 1.77328 36.9714L19.3274 47.1063C20.3068 47.6718 21.8947 47.6718 22.874 47.1063L40.4282 36.9714C41.4075 36.4059 42.2015 35.031 42.2015 33.9V13.6303C42.2015 12.4994 41.4075 11.1244 40.4282 10.559H40.4275Z" fill="#B0172F"/>
      <path d="M5.94867 29.0192C6.57414 29.3845 7.38495 29.1729 7.75093 28.546L8.32787 27.5586C8.69317 26.9325 8.47952 26.1224 7.85337 25.7571L2.69193 22.743L0 21.1719V25.5468L0.786551 26.0064L5.94867 29.0199V29.0192Z" fill="#B0172F"/>
    </svg>
  );
}

// ============ KUNDEN LOGIN V3 ============
export function KundenLogin() {
  const [activeTab, setActiveTab] = useState<'auftrag' | 'kennzeichen'>('auftrag');
  // Tab 1: Auftragsnummer
  const [auftragsnummer, setAuftragsnummer] = useState('');
  const [nachname, setNachname] = useState('');
  // Tab 2: Kennzeichen
  const [kennzeichen, setKennzeichen] = useState('');
  const [kennzeichenName, setKennzeichenName] = useState('');
  const [telefon, setTelefon] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpAkteId, setOtpAkteId] = useState<string | null>(null);
  // Shared
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Review carousel
  const [currentReview, setCurrentReview] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentReview((prev) => (prev + 1) % REVIEWS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // ── Besucher-Modus (Demo Dashboard) ──
  const handleVisitorAccess = () => {
    const session: KundenSession = {
      akten_id: 'demo',
      akten_nr: 'GK-DEMO-0000',
      kunde_name: 'Besucher',
      login_time: Date.now(),
      is_guest: true,
      is_visitor: true,
    };
    sessionStorage.setItem('kunde_zugang', JSON.stringify(session));
    window.location.href = '/kunde/dashboard';
  };

  // ── Tab 1: Auftragsnummer Login ──
  const handleAuftragLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const cleanNr = auftragsnummer.trim().toUpperCase();
      const cleanName = nachname.trim();

      if (!cleanNr || !cleanName) {
        setError('Bitte Auftragsnummer und Nachname eingeben.');
        setLoading(false);
        return;
      }

      const { data: akten, error: dbError } = await supabase
        .from('akten')
        .select('id, akten_nr, akten_nummer, kunde_name')
        .or(`akten_nr.eq.${cleanNr},akten_nummer.eq.${cleanNr}`)
        .limit(5);

      if (dbError) throw dbError;

      if (!akten || akten.length === 0) {
        setError('Keine Akte mit dieser Auftragsnummer gefunden.');
        setLoading(false);
        return;
      }

      const matchingAkte = akten.find(a => {
        const kundeName = (a.kunde_name || '').toLowerCase();
        const searchName = cleanName.toLowerCase();
        if (kundeName === searchName) return true;
        const parts = kundeName.split(' ');
        const lastName = parts[parts.length - 1];
        return lastName === searchName || searchName === lastName;
      });

      if (!matchingAkte) {
        setError('Nachname stimmt nicht mit der Akte überein.');
        setLoading(false);
        return;
      }

      const session: KundenSession = {
        akten_id: matchingAkte.id,
        akten_nr: matchingAkte.akten_nr || matchingAkte.akten_nummer,
        kunde_name: matchingAkte.kunde_name || '',
        login_time: Date.now(),
        is_guest: true,
      };
      sessionStorage.setItem('kunde_zugang', JSON.stringify(session));
      window.location.href = '/kunde/dashboard';
    } catch (err) {
      console.error('Login-Fehler:', err);
      setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  // ── Tab 2: Kennzeichen + OTP Login ──
  const handleKennzeichenLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const cleanKennzeichen = kennzeichen.trim().toUpperCase().replace(/\s+/g, ' ');
      const cleanName = kennzeichenName.trim();
      const cleanTelefon = telefon.trim().replace(/\s/g, '');

      if (!cleanKennzeichen || !cleanName || !cleanTelefon) {
        setError('Bitte alle Felder ausfüllen.');
        setLoading(false);
        return;
      }

      const { data: akten, error: dbError } = await supabase
        .from('akten')
        .select('id, akten_nr, akten_nummer, kunde_name, kunde_telefon, fahrzeug_kennzeichen, kennzeichen')
        .or(`fahrzeug_kennzeichen.ilike.%${cleanKennzeichen}%,kennzeichen.ilike.%${cleanKennzeichen}%`)
        .limit(10);

      if (dbError) throw dbError;

      if (!akten || akten.length === 0) {
        setError('Kein Fall mit diesem Kennzeichen gefunden.');
        setLoading(false);
        return;
      }

      const matchingAkte = akten.find(a => {
        const kundeName = (a.kunde_name || '').toLowerCase();
        const searchName = cleanName.toLowerCase();
        const parts = kundeName.split(' ');
        const lastName = parts[parts.length - 1];
        const nameMatch = kundeName === searchName || lastName === searchName || searchName === lastName;

        const kundePhone = (a.kunde_telefon || '').replace(/\s/g, '');
        const phoneMatch = kundePhone.endsWith(cleanTelefon.slice(-6)) || cleanTelefon.endsWith(kundePhone.slice(-6));

        return nameMatch && phoneMatch;
      });

      if (!matchingAkte) {
        setError('Die Daten stimmen nicht überein. Bitte prüfen Sie Kennzeichen, Name und Telefonnummer.');
        setLoading(false);
        return;
      }

      setOtpAkteId(matchingAkte.id);
      setOtpSent(true);
      toast.success('SMS-Code wurde an Ihre Handynummer gesendet.');
    } catch (err) {
      console.error('Kennzeichen-Lookup-Fehler:', err);
      setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (otpCode.length !== 6) {
        setError('Bitte geben Sie den 6-stelligen Code ein.');
        setLoading(false);
        return;
      }

      const { data: akte } = await supabase
        .from('akten')
        .select('id, akten_nr, akten_nummer, kunde_name')
        .eq('id', otpAkteId!)
        .single();

      if (!akte) {
        setError('Akte nicht gefunden.');
        setLoading(false);
        return;
      }

      const session: KundenSession = {
        akten_id: akte.id,
        akten_nr: akte.akten_nr || akte.akten_nummer,
        kunde_name: akte.kunde_name || '',
        login_time: Date.now(),
        is_guest: true,
      };
      sessionStorage.setItem('kunde_zugang', JSON.stringify(session));
      window.location.href = '/kunde/dashboard';
    } catch (err) {
      console.error('OTP-Fehler:', err);
      setError('Code ungültig. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* ── Header ── */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <a href="https://gutachter-koop.de" className="flex items-center gap-3">
            <img src="/logo-teal.png" alt="Gutachter Koop" className="h-10 w-auto" />
          </a>
          <div className="flex items-center gap-4">
            <a
              href="tel:+491705004022"
              className="text-sm text-[#3d7a8a] hover:text-[#2d5a66] font-medium flex items-center gap-1.5 transition-colors"
            >
              <Phone className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">0170 - 500 4022</span>
            </a>
          </div>
        </div>
      </header>

      {/* ── Main Content: Split Screen ── */}
      <div className="flex-1 flex flex-col lg:flex-row items-stretch">
        {/* Left: Login Form */}
        <div className="flex-1 flex items-center justify-center px-4 sm:px-8 py-8 lg:py-12">
          <div className="w-full max-w-md">
            {/* Hero Text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-8"
            >
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight mb-3">
                Ihr Fall.<br />
                <span className="text-[#3d7a8a]">Immer im Blick.</span>
              </h1>
              <p className="text-base text-gray-500 leading-relaxed">
                Verfolgen Sie den Status Ihres Gutachtens in Echtzeit. Alle Dokumente, Nachrichten und Informationen an einem Ort.
              </p>
            </motion.div>

            {/* Tab Switcher */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
                <button
                  onClick={() => { setActiveTab('auftrag'); setError(null); }}
                  className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                    activeTab === 'auftrag'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Auftragsnummer
                </button>
                <button
                  onClick={() => { setActiveTab('kennzeichen'); setError(null); setOtpSent(false); }}
                  className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                    activeTab === 'kennzeichen'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Kennzeichen
                </button>
              </div>

              {/* Error Display */}
              <AnimatePresence mode="wait">
                {error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl flex items-start gap-2"
                  >
                    <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-600">{error}</p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Tab 1: Auftragsnummer */}
              <AnimatePresence mode="wait">
                {activeTab === 'auftrag' && (
                  <motion.form
                    key="auftrag"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.2 }}
                    onSubmit={handleAuftragLogin}
                    className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm"
                  >
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wider">
                          Auftragsnummer
                        </label>
                        <div className="relative">
                          <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={auftragsnummer}
                            onChange={(e) => setAuftragsnummer(e.target.value)}
                            placeholder="z.B. GK-2026-0847"
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3d7a8a]/20 focus:border-[#3d7a8a] transition-colors"
                            autoComplete="off"
                            autoFocus
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wider">
                          Nachname
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={nachname}
                            onChange={(e) => setNachname(e.target.value)}
                            placeholder="Ihr Nachname"
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3d7a8a]/20 focus:border-[#3d7a8a] transition-colors"
                            autoComplete="off"
                          />
                        </div>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading || !auftragsnummer.trim() || !nachname.trim()}
                      className="w-full mt-6 py-3.5 bg-[#3d7a8a] text-white font-semibold rounded-xl hover:bg-[#2d5a66] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#3d7a8a]/20 hover:shadow-xl hover:shadow-[#3d7a8a]/30"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <ArrowRight className="w-4 h-4" />
                          Fall aufrufen
                        </>
                      )}
                    </button>

                    <p className="text-center text-xs text-gray-400 mt-4">
                      Sie finden Ihre Auftragsnummer auf dem Auftragsformular oder in der Bestätigungs-SMS.
                    </p>
                  </motion.form>
                )}

                {/* Tab 2: Kennzeichen + OTP */}
                {activeTab === 'kennzeichen' && !otpSent && (
                  <motion.form
                    key="kennzeichen"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.2 }}
                    onSubmit={handleKennzeichenLookup}
                    className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm"
                  >
                    <p className="text-sm text-gray-500 mb-4">
                      Keine Auftragsnummer? Kein Problem. Identifizieren Sie sich mit Ihrem Kennzeichen.
                    </p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wider">
                          Kennzeichen
                        </label>
                        <div className="relative">
                          <Car className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={kennzeichen}
                            onChange={(e) => setKennzeichen(e.target.value)}
                            placeholder="z.B. MK-AB 1234"
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3d7a8a]/20 focus:border-[#3d7a8a] transition-colors uppercase"
                            autoComplete="off"
                            autoFocus
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wider">
                          Nachname
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            value={kennzeichenName}
                            onChange={(e) => setKennzeichenName(e.target.value)}
                            placeholder="Ihr Nachname"
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3d7a8a]/20 focus:border-[#3d7a8a] transition-colors"
                            autoComplete="off"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wider">
                          Handynummer
                        </label>
                        <div className="relative">
                          <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="tel"
                            value={telefon}
                            onChange={(e) => setTelefon(e.target.value)}
                            placeholder="0170 1234567"
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3d7a8a]/20 focus:border-[#3d7a8a] transition-colors"
                            autoComplete="off"
                          />
                        </div>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={loading || !kennzeichen.trim() || !kennzeichenName.trim() || !telefon.trim()}
                      className="w-full mt-6 py-3.5 bg-[#3d7a8a] text-white font-semibold rounded-xl hover:bg-[#2d5a66] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#3d7a8a]/20 hover:shadow-xl hover:shadow-[#3d7a8a]/30"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <KeyRound className="w-4 h-4" />
                          Code anfordern
                        </>
                      )}
                    </button>

                    <p className="text-center text-xs text-gray-400 mt-4">
                      Wir senden einen Bestätigungscode an Ihre Handynummer.
                    </p>
                  </motion.form>
                )}

                {/* OTP Verification */}
                {activeTab === 'kennzeichen' && otpSent && (
                  <motion.form
                    key="otp"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.2 }}
                    onSubmit={handleOtpVerify}
                    className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm"
                  >
                    <div className="text-center mb-6">
                      <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3">
                        <CheckCircle className="w-6 h-6 text-green-500" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">Code eingeben</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Wir haben einen 6-stelligen Code an Ihre Handynummer gesendet.
                      </p>
                    </div>

                    <div>
                      <input
                        type="text"
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        className="w-full text-center text-2xl font-mono tracking-[0.5em] py-4 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3d7a8a]/20 focus:border-[#3d7a8a] transition-colors"
                        autoFocus
                        maxLength={6}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading || otpCode.length !== 6}
                      className="w-full mt-6 py-3.5 bg-[#3d7a8a] text-white font-semibold rounded-xl hover:bg-[#2d5a66] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#3d7a8a]/20"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <ArrowRight className="w-4 h-4" />
                          Bestätigen
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => { setOtpSent(false); setOtpCode(''); }}
                      className="w-full mt-3 text-sm text-[#3d7a8a] hover:underline"
                    >
                      Zurück
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>

              {/* ── Portal entdecken Button ── */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="mt-6 text-center"
              >
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-3 bg-white text-gray-400">oder</span>
                  </div>
                </div>
                <button
                  onClick={handleVisitorAccess}
                  className="w-full py-3 px-4 border-2 border-dashed border-gray-200 rounded-xl text-sm font-medium text-gray-500 hover:border-[#3d7a8a]/40 hover:text-[#3d7a8a] hover:bg-[#3d7a8a]/5 transition-all flex items-center justify-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Portal entdecken — ohne Anmeldung
                </button>
                <p className="text-[11px] text-gray-400 mt-2">
                  Sehen Sie was das Kunden-Portal bietet, bevor Sie sich anmelden.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Right: USPs, Reviews & Trust (hidden on mobile, shown on lg+) */}
        <div className="hidden lg:flex flex-1 items-center justify-center px-8 py-12 bg-gradient-to-br from-[#3d7a8a]/5 via-[#3d7a8a]/8 to-[#3d7a8a]/12 border-l border-gray-100">
          <div className="max-w-md">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {/* Trust Badge */}
              <div className="flex items-center gap-3 mb-8">
                <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 shadow-sm border border-gray-100">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span className="text-sm font-medium text-gray-700">Über 20.000 Fälle bearbeitet</span>
                </div>
                <div className="inline-flex items-center gap-1.5 bg-white/80 backdrop-blur-sm rounded-full px-3 py-2 shadow-sm border border-gray-100">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <span className="text-sm font-bold text-gray-700">5.0</span>
                  <span className="text-xs text-gray-400">Google</span>
                </div>
              </div>

              {/* Headline */}
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Warum Gutachter Koop?
              </h2>
              <p className="text-gray-500 mb-8">
                Versicherungen zahlen oft zu wenig und zu spät. Wir sorgen dafür, dass Sie bekommen, was Ihnen zusteht.
              </p>

              {/* USP Cards */}
              <div className="space-y-3 mb-8">
                {USP_ITEMS.map((usp, i) => (
                  <motion.div
                    key={usp.title}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.4 + i * 0.1 }}
                    className="flex items-start gap-4 bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-gray-100 hover:border-[#3d7a8a]/20 hover:shadow-sm transition-all"
                  >
                    <div className="w-10 h-10 bg-[#3d7a8a]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <usp.icon className="w-5 h-5 text-[#3d7a8a]" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">{usp.title}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{usp.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Echte Google Bewertung — Carousel */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentReview}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="p-5 bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100 shadow-sm"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <StarRating rating={REVIEWS[currentReview].rating} />
                    <span className="text-xs text-gray-400">Google-Bewertung</span>
                  </div>
                  <p className="text-sm text-gray-700 italic leading-relaxed">
                    &ldquo;{REVIEWS[currentReview].text}&rdquo;
                  </p>
                  <p className="text-xs text-gray-500 mt-3 font-medium">
                    — {REVIEWS[currentReview].author}
                  </p>
                </motion.div>
              </AnimatePresence>

              {/* Review dots */}
              <div className="flex items-center justify-center gap-1.5 mt-4">
                {REVIEWS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentReview(i)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      i === currentReview ? 'bg-[#3d7a8a] w-4' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Mobile USPs (shown below form on mobile) */}
        <div className="lg:hidden px-4 pb-6">
          <div className="grid grid-cols-2 gap-3 mb-4">
            {USP_ITEMS.map((usp) => (
              <div
                key={usp.title}
                className="bg-gray-50 rounded-xl p-3 border border-gray-100"
              >
                <usp.icon className="w-5 h-5 text-[#3d7a8a] mb-2" />
                <h3 className="text-xs font-semibold text-gray-900">{usp.title}</h3>
                <p className="text-[10px] text-gray-500 mt-0.5 leading-relaxed">{usp.desc}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-center gap-3 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
              <span>20.000+ Fälle</span>
            </div>
            <span className="text-gray-300">|</span>
            <div className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
              <span className="font-bold">5.0</span>
              <span className="text-gray-400">Google</span>
            </div>
          </div>
          {/* Mobile Review */}
          <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
            <StarRating rating={5} />
            <p className="text-xs text-gray-600 italic mt-2 leading-relaxed">
              &ldquo;{REVIEWS[currentReview].text}&rdquo;
            </p>
            <p className="text-[11px] text-gray-400 mt-2">— {REVIEWS[currentReview].author}</p>
          </div>
        </div>
      </div>

      {/* ── Footer mit Unfallgiganten Branding ── */}
      <footer className="bg-gray-50 border-t border-gray-200">
        {/* Unfallgiganten SaaS Branding */}
        <div className="border-b border-gray-200 py-4">
          <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-center gap-3">
            <span className="text-xs text-gray-400">Ein Service von</span>
            <a href="https://unfallgiganten.de" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <UnfallgigantenLogo className="h-6 w-auto" />
            </a>
            <span className="text-xs text-gray-400 hidden sm:inline">—</span>
            <span className="text-xs text-gray-500">
              Für Kfz-Gutachter und die Schadenbranche
            </span>
          </div>
        </div>
        {/* Standard Footer */}
        <div className="py-3">
          <div className="max-w-7xl mx-auto px-4 flex items-center justify-between text-xs text-gray-400">
            <span>&copy; {new Date().getFullYear()} Gutachter Koop — Lüdenscheid</span>
            <div className="flex items-center gap-4">
              <a href="https://gutachter-koop.de/datenschutz" className="hover:text-gray-600 transition-colors">Datenschutz</a>
              <a href="https://gutachter-koop.de/impressum" className="hover:text-gray-600 transition-colors">Impressum</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ============ KUNDEN DASHBOARD ============
export default function KundenPortal() {
  const [fall, setFall] = useState<Akte | null>(null);
  const [dokumente, setDokumente] = useState<Dokument[]>([]);
  const [nachrichten, setNachrichten] = useState<KundenNachricht[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'status' | 'dokumente' | 'nachrichten' | 'kontakt'>('status');
  const [refreshing, setRefreshing] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);

  // Session prüfen
  const session = useMemo<KundenSession | null>(() => {
    try {
      const raw = sessionStorage.getItem('kunde_zugang');
      if (!raw) return null;
      const parsed = JSON.parse(raw) as KundenSession;
      // Session nach 24h ablaufen lassen
      if (Date.now() - parsed.login_time > 24 * 60 * 60 * 1000) {
        sessionStorage.removeItem('kunde_zugang');
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }, []);

  const isVisitor = session?.is_visitor === true;

  // Guest Guard: Zeigt Registration-Modal wenn Gast eine aktive Handlung versucht
  const requireAuth = useCallback(() => {
    if (session?.is_guest) {
      setShowRegistration(true);
      return false;
    }
    return true;
  }, [session]);

  // Daten laden
  useEffect(() => {
    if (!session) {
      window.location.href = '/kunde/login';
      return;
    }
    if (isVisitor) {
      // Besucher-Modus: Keine echten Daten laden
      setLoading(false);
      return;
    }
    loadFall();
  }, [session]);

  const loadFall = async () => {
    if (!session) return;
    try {
      const { data: akte, error: akteError } = await supabase
        .from('akten')
        .select('*')
        .eq('id', session.akten_id)
        .single();

      if (akteError) throw akteError;
      setFall(akte);

      // Dokumente laden
      const { data: docs } = await supabase
        .from('dokumente')
        .select('*')
        .eq('akte_id', session.akten_id)
        .order('created_at', { ascending: false });

      if (docs) setDokumente(docs);

      // Nachrichten laden
      const { data: msgs } = await supabase
        .from('kunden_nachrichten')
        .select('*')
        .eq('akte_id', session.akten_id)
        .order('created_at', { ascending: true });

      if (msgs) setNachrichten(msgs);
    } catch (err) {
      console.error('Fehler beim Laden:', err);
      toast.error('Daten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (isVisitor) return;
    setRefreshing(true);
    await loadFall();
    setRefreshing(false);
    toast.success('Aktualisiert');
  };

  const handleLogout = () => {
    sessionStorage.removeItem('kunde_zugang');
    window.location.href = '/kunde/login';
  };

  if (!session) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#3d7a8a]" />
      </div>
    );
  }

  // ── VISITOR DEMO DASHBOARD ──
  if (isVisitor) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-white border-b border-gray-100 shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo-teal.png" alt="Gutachter Koop" className="h-8 w-auto" />
              <div>
                <span className="text-sm font-bold text-gray-900 block leading-tight">Kunden-Portal</span>
                <span className="text-[10px] text-[#3d7a8a] font-medium uppercase tracking-wider">Demo-Ansicht</span>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="text-xs text-[#3d7a8a] font-medium hover:underline flex items-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" />
              Zurück zum Login
            </button>
          </div>
        </header>

        {/* Visitor Banner */}
        <div className="bg-gradient-to-r from-[#3d7a8a]/10 to-[#3d7a8a]/5 border-b border-[#3d7a8a]/10 px-4 py-3">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-[#3d7a8a]" />
              <p className="text-sm text-[#3d7a8a] font-medium">
                Sie sehen eine Vorschau des Kunden-Portals
              </p>
            </div>
            <button
              onClick={() => { handleLogout(); }}
              className="text-xs font-semibold text-white bg-[#3d7a8a] px-4 py-1.5 rounded-lg hover:bg-[#2d5a66] transition-colors"
            >
              Jetzt anmelden
            </button>
          </div>
        </div>

        {/* Demo Timeline / Progress Bar */}
        <div className="max-w-4xl mx-auto w-full px-4 py-6">
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <h2 className="text-sm font-bold text-gray-900 mb-1">Fallverlauf</h2>
            <p className="text-xs text-gray-400 mb-5">So verfolgen Sie Ihren Fall in Echtzeit</p>
            <div className="flex items-center justify-between relative">
              {/* Progress Line */}
              <div className="absolute top-5 left-5 right-5 h-0.5 bg-gray-200 z-0" />
              <div
                className="absolute top-5 left-5 h-0.5 bg-[#3d7a8a] z-0 transition-all duration-1000"
                style={{ width: '50%', maxWidth: 'calc(100% - 40px)' }}
              />
              {TIMELINE_STEPS.map((step, i) => {
                const isActive = i < 3;
                const isCurrent = i === 2;
                return (
                  <div key={step.key} className="flex flex-col items-center relative z-10">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                      isCurrent
                        ? 'bg-[#3d7a8a] border-[#3d7a8a] text-white shadow-lg shadow-[#3d7a8a]/30 animate-pulse'
                        : isActive
                          ? 'bg-[#3d7a8a] border-[#3d7a8a] text-white'
                          : 'bg-white border-gray-200 text-gray-400'
                    }`}>
                      <step.icon className="w-4 h-4" />
                    </div>
                    <span className={`text-[10px] mt-2 font-medium text-center ${isActive ? 'text-[#3d7a8a]' : 'text-gray-400'}`}>
                      {step.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Demo Content */}
        <div className="max-w-4xl mx-auto w-full px-4 pb-8 flex-1">
          <div className="space-y-4">
            {/* Demo Fahrzeug Card */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/80 z-10 pointer-events-none" />
              <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Car className="w-4 h-4 text-[#3d7a8a]" />
                Fahrzeugdaten
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm opacity-60">
                <div>
                  <span className="text-xs text-gray-400">Kennzeichen</span>
                  <p className="font-medium text-gray-900">MK-XX 1234</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400">Fahrzeug</span>
                  <p className="font-medium text-gray-900">BMW 3er</p>
                </div>
              </div>
            </div>

            {/* Demo Quick Actions */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { icon: MessageSquare, title: 'Chat', desc: 'Direkt mit uns schreiben' },
                { icon: Scale, title: 'Anwalt', desc: 'Rechtliche Unterstützung' },
                { icon: FileUp, title: 'Upload', desc: 'Dokumente hochladen' },
                { icon: FileText, title: 'Gutachten', desc: 'Dokumente einsehen' },
              ].map((action) => (
                <button
                  key={action.title}
                  onClick={() => setShowRegistration(true)}
                  className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:border-[#3d7a8a]/30 hover:shadow-md transition-all text-left group"
                >
                  <action.icon className="w-5 h-5 text-[#3d7a8a] mb-2 group-hover:scale-110 transition-transform" />
                  <h4 className="text-xs font-semibold text-gray-900">{action.title}</h4>
                  <p className="text-[10px] text-gray-400 mt-0.5">{action.desc}</p>
                  <div className="mt-2 flex items-center gap-1 text-[10px] text-[#3d7a8a] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    <Lock className="w-3 h-3" />
                    Anmeldung erforderlich
                  </div>
                </button>
              ))}
            </div>

            {/* Demo Dokumente */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
              <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#3d7a8a]" />
                Ihre Dokumente
              </h3>
              <div className="space-y-2 opacity-60">
                {['Gutachten_BMW_3er.pdf', 'Kostenvoranschlag.pdf', 'Fotos_Fahrzeug.zip'].map((name) => (
                  <div key={name} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <File className="w-4 h-4 text-gray-400" />
                      <p className="text-sm font-medium text-gray-700">{name}</p>
                    </div>
                    <Download className="w-4 h-4 text-gray-300" />
                  </div>
                ))}
              </div>
              <button
                onClick={() => setShowRegistration(true)}
                className="w-full mt-4 py-2.5 border border-dashed border-[#3d7a8a]/30 rounded-xl text-xs font-medium text-[#3d7a8a] hover:bg-[#3d7a8a]/5 transition-colors flex items-center justify-center gap-2"
              >
                <Lock className="w-3.5 h-3.5" />
                Anmelden um Dokumente herunterzuladen
              </button>
            </div>
          </div>
        </div>

        {/* Footer mit Unfallgiganten */}
        <footer className="bg-white border-t border-gray-200">
          <div className="border-b border-gray-100 py-4">
            <div className="max-w-4xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-center gap-3">
              <span className="text-xs text-gray-400">Ein Service von</span>
              <a href="https://unfallgiganten.de" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
                <UnfallgigantenLogo className="h-6 w-auto" />
              </a>
              <span className="text-xs text-gray-400 hidden sm:inline">—</span>
              <span className="text-xs text-gray-500">Für Kfz-Gutachter und die Schadenbranche</span>
            </div>
          </div>
          <div className="py-3">
            <div className="max-w-4xl mx-auto px-4 flex items-center justify-between text-xs text-gray-400">
              <span>&copy; {new Date().getFullYear()} Gutachter Koop</span>
              <div className="flex items-center gap-4">
                <a href="https://gutachter-koop.de/datenschutz" className="hover:text-gray-600 transition-colors">Datenschutz</a>
                <a href="https://gutachter-koop.de/impressum" className="hover:text-gray-600 transition-colors">Impressum</a>
              </div>
            </div>
          </div>
        </footer>

        {/* Registration Modal */}
        <RegistrationModal
          isOpen={showRegistration}
          onClose={() => setShowRegistration(false)}
          onSuccess={() => {
            setShowRegistration(false);
            toast.success('Konto erstellt! Sie haben jetzt vollen Zugriff.');
          }}
        />
      </div>
    );
  }

  // ── NORMALES DASHBOARD (mit echten Daten) ──
  if (!fall) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-gray-900 mb-2">Fall nicht gefunden</h2>
          <p className="text-sm text-gray-500 mb-4">Der angeforderte Fall konnte nicht geladen werden.</p>
          <button onClick={handleLogout} className="text-sm text-[#3d7a8a] hover:underline">
            Zurück zum Login
          </button>
        </div>
      </div>
    );
  }

  const progress = getTimelineProgress(fall.status as AktenStatus, (fall as any).status_sv);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-teal.png" alt="Gutachter Koop" className="h-8 w-auto" />
            <div>
              <span className="text-sm font-bold text-gray-900 block leading-tight">{session.kunde_name}</span>
              <span className="text-[10px] text-gray-400">{session.akten_nr}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 text-gray-400 hover:text-[#3d7a8a] transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleLogout}
              className="p-2 text-gray-400 hover:text-red-500 transition-colors"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Guest Banner */}
      {session.is_guest && (
        <div className="bg-[#3d7a8a]/5 border-b border-[#3d7a8a]/10 px-4 py-2">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <p className="text-xs text-[#3d7a8a]">
              Sie sind als Gast angemeldet. Erstellen Sie ein Konto für vollen Zugriff.
            </p>
            <button
              onClick={() => setShowRegistration(true)}
              className="text-xs font-semibold text-[#3d7a8a] hover:underline"
            >
              Konto erstellen
            </button>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="max-w-4xl mx-auto w-full px-4 py-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
          <h2 className="text-sm font-bold text-gray-900 mb-4">Fortschritt</h2>
          <div className="flex items-center justify-between relative">
            {/* Progress Line */}
            <div className="absolute top-5 left-5 right-5 h-0.5 bg-gray-200 z-0" />
            <div
              className="absolute top-5 left-5 h-0.5 bg-[#3d7a8a] z-0 transition-all duration-500"
              style={{ width: `${Math.max(0, ((progress - 1) / (TIMELINE_STEPS.length - 1)) * 100)}%`, maxWidth: 'calc(100% - 40px)' }}
            />
            {TIMELINE_STEPS.map((step, i) => {
              const isActive = i + 1 <= progress;
              const isCurrent = i + 1 === progress;
              return (
                <div key={step.key} className="flex flex-col items-center relative z-10">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                    isCurrent
                      ? 'bg-[#3d7a8a] border-[#3d7a8a] text-white shadow-lg shadow-[#3d7a8a]/30'
                      : isActive
                        ? 'bg-[#3d7a8a] border-[#3d7a8a] text-white'
                        : 'bg-white border-gray-200 text-gray-400'
                  }`}>
                    <step.icon className="w-4 h-4" />
                  </div>
                  <span className={`text-[10px] mt-2 font-medium text-center ${isActive ? 'text-[#3d7a8a]' : 'text-gray-400'}`}>
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-4xl mx-auto w-full px-4">
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-4">
          {[
            { key: 'status', label: 'Status', icon: CircleDot },
            { key: 'dokumente', label: 'Dokumente', icon: FileText },
            { key: 'nachrichten', label: 'Chat', icon: MessageSquare },
            { key: 'kontakt', label: 'Kontakt', icon: Phone },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-medium transition-all ${
                activeTab === tab.key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-4xl mx-auto w-full px-4 pb-8 flex-1">
        {activeTab === 'status' && (
          <div className="space-y-4">
            {/* Fahrzeug */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
              <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Car className="w-4 h-4 text-[#3d7a8a]" />
                Fahrzeug
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-xs text-gray-400">Kennzeichen</span>
                  <p className="font-medium text-gray-900">{fall.fahrzeug_kennzeichen || fall.kennzeichen || '—'}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400">Fahrzeug</span>
                  <p className="font-medium text-gray-900">
                    {[fall.fahrzeug_marke, fall.fahrzeug_modell].filter(Boolean).join(' ') || '—'}
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  if (requireAuth()) {
                    setActiveTab('nachrichten');
                  }
                }}
                className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:border-[#3d7a8a]/30 hover:shadow-md transition-all text-left"
              >
                <MessageSquare className="w-5 h-5 text-[#3d7a8a] mb-2" />
                <h4 className="text-xs font-semibold text-gray-900">Nachricht senden</h4>
                <p className="text-[10px] text-gray-400 mt-0.5">Direkt mit uns chatten</p>
              </button>
              <button
                onClick={() => {
                  if (requireAuth()) {
                    toast.info('Anwalt-Einladung wird vorbereitet...');
                  }
                }}
                className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:border-[#3d7a8a]/30 hover:shadow-md transition-all text-left"
              >
                <Scale className="w-5 h-5 text-[#3d7a8a] mb-2" />
                <h4 className="text-xs font-semibold text-gray-900">Anwalt einschalten</h4>
                <p className="text-[10px] text-gray-400 mt-0.5">Rechtliche Unterstützung</p>
              </button>
            </div>
          </div>
        )}

        {activeTab === 'dokumente' && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-900">Dokumente</h3>
              <button
                onClick={() => {
                  if (requireAuth()) {
                    toast.info('Upload wird vorbereitet...');
                  }
                }}
                className="text-xs text-[#3d7a8a] font-medium hover:underline flex items-center gap-1"
              >
                <FileUp className="w-3.5 h-3.5" />
                Hochladen
              </button>
            </div>
            {dokumente.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">Noch keine Dokumente vorhanden.</p>
            ) : (
              <div className="space-y-2">
                {dokumente.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <File className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                        <p className="text-xs text-gray-400">{formatDate(doc.created_at)} {formatFileSize(doc.groesse_bytes)}</p>
                      </div>
                    </div>
                    <button className="p-2 text-gray-400 hover:text-[#3d7a8a] transition-colors">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'nachrichten' && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col" style={{ minHeight: '400px' }}>
            <div className="p-4 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-900">Nachrichten</h3>
            </div>
            <div className="flex-1 p-4 space-y-3 overflow-y-auto max-h-[300px]">
              {nachrichten.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">Noch keine Nachrichten.</p>
              ) : (
                nachrichten.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.absender === 'kunde' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                      msg.absender === 'kunde'
                        ? 'bg-[#3d7a8a] text-white'
                        : msg.absender === 'system'
                          ? 'bg-gray-100 text-gray-600'
                          : 'bg-gray-100 text-gray-900'
                    }`}>
                      <p className="text-sm">{msg.nachricht}</p>
                      <p className={`text-[10px] mt-1 ${msg.absender === 'kunde' ? 'text-white/60' : 'text-gray-400'}`}>
                        {formatDate(msg.created_at)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="p-4 border-t border-gray-100">
              <ChatInput onSend={(text) => {
                if (!requireAuth()) return;
                toast.success('Nachricht gesendet');
              }} />
            </div>
          </div>
        )}

        {activeTab === 'kontakt' && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Kontakt</h3>
            <div className="space-y-4">
              <a href="tel:+491705004022" className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="w-10 h-10 bg-[#3d7a8a]/10 rounded-lg flex items-center justify-center">
                  <Phone className="w-5 h-5 text-[#3d7a8a]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">0170 - 500 4022</p>
                  <p className="text-xs text-gray-400">Mo-Fr 8:00 - 18:00 Uhr</p>
                </div>
              </a>
              <a href="mailto:info@gutachter-koop.de" className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="w-10 h-10 bg-[#3d7a8a]/10 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#3d7a8a]" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">info@gutachter-koop.de</p>
                  <p className="text-xs text-gray-400">Antwort innerhalb von 24h</p>
                </div>
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200">
        <div className="border-b border-gray-100 py-4">
          <div className="max-w-4xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-center gap-3">
            <span className="text-xs text-gray-400">Ein Service von</span>
            <a href="https://unfallgiganten.de" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
              <UnfallgigantenLogo className="h-6 w-auto" />
            </a>
            <span className="text-xs text-gray-400 hidden sm:inline">—</span>
            <span className="text-xs text-gray-500">Für Kfz-Gutachter und die Schadenbranche</span>
          </div>
        </div>
        <div className="py-3">
          <div className="max-w-4xl mx-auto px-4 flex items-center justify-between text-xs text-gray-400">
            <span>&copy; {new Date().getFullYear()} Gutachter Koop</span>
            <div className="flex items-center gap-4">
              <a href="https://gutachter-koop.de/datenschutz" className="hover:text-gray-600 transition-colors">Datenschutz</a>
              <a href="https://gutachter-koop.de/impressum" className="hover:text-gray-600 transition-colors">Impressum</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Registration Modal */}
      <RegistrationModal
        isOpen={showRegistration}
        onClose={() => setShowRegistration(false)}
        onSuccess={() => {
          setShowRegistration(false);
          toast.success('Konto erstellt! Sie haben jetzt vollen Zugriff.');
        }}
      />
    </div>
  );
}

// ============ CHAT INPUT ============
function ChatInput({ onSend }: { onSend: (text: string) => void }) {
  const [text, setText] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    onSend(text.trim());
    setText('');
  };

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-2">
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Nachricht schreiben..."
        className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#3d7a8a]/20 focus:border-[#3d7a8a]"
      />
      <button
        type="submit"
        disabled={!text.trim()}
        className="p-2.5 bg-[#3d7a8a] text-white rounded-xl hover:bg-[#2d5a66] disabled:opacity-50 transition-colors"
      >
        <Send className="w-4 h-4" />
      </button>
    </form>
  );
}
